import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import {
    AutoComplete,
    Button,
    Form,
    Layout,
    Modal,
    message,
    Select,
    Space,
    Switch,
    Table,
} from 'antd';
import { FC, useEffect, useState } from 'react';

import {
    FacilityDto,
    FeatureFlagDto,
    FeatureFlagUpdateDto,
} from '@clh/api-client';
import { useApiClient } from '@clh/ui';
import { FEATURE_FLAGS } from '@clh/util';

import { Content, Header } from '../layout';
import Spinner from '../spinner/spinner';

export const FeatureFlags: FC = () => {
    const api = useApiClient();
    const [featureFlags, setFeatureFlags] = useState<FeatureFlagDto[]>([]);
    const [facilities, setFacilities] = useState<FacilityDto[]>([]);
    const [loading, setLoading] = useState(true);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingFlag, setEditingFlag] = useState<FeatureFlagDto | null>(null);
    const [form] = Form.useForm();

    const fetchFeatureFlags = async () => {
        if (!api) return;
        try {
            setLoading(true);
            const response = await api.featureFlagControllerGetFeatureFlags();
            setFeatureFlags(response);
        } catch (_error) {
            message.error('Failed to fetch feature flags');
        } finally {
            setLoading(false);
        }
    };

    const fetchFacilities = async () => {
        if (!api) return;
        try {
            const response = await api.facilityControllerGetFacilities();
            setFacilities(response);
        } catch (_error) {
            message.error('Failed to fetch feature flags');
        }
    };

    useEffect(() => {
        void fetchFeatureFlags();
        void fetchFacilities();
    }, [api]);

    const handleAddFlag = () => {
        setEditingFlag(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    const handleEditFlag = (flag: FeatureFlagDto) => {
        setEditingFlag(flag);
        form.setFieldsValue({
            key: flag.key,
            isActiveForAll: flag.isActiveForAll,
            activeFacilityIds: flag.activeFacilities.map((f) => f.id),
        });
        setIsModalVisible(true);
    };

    const handleDeleteFlag = async (key: string) => {
        try {
            await api?.featureFlagControllerDeleteFeatureFlag({ flagKey: key });
            message.success('Feature flag deleted successfully');
            void fetchFeatureFlags();
        } catch (_error) {
            message.error('Failed to delete feature flag');
        }
    };

    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();
            const updateDto: FeatureFlagUpdateDto = {
                key: values.key,
                isActiveForAll: values.isActiveForAll,
                activeFacilityIds: values.activeFacilityIds?.map(String) || [],
            };

            if (editingFlag) {
                // Update existing flag
                await api?.featureFlagControllerUpdateFeatureFlag({
                    featureFlagUpdateDto: updateDto,
                });
                message.success('Feature flag updated successfully');
            } else {
                // Create new flag
                await api?.featureFlagControllerUpdateFeatureFlag({
                    featureFlagUpdateDto: updateDto,
                });
                message.success('Feature flag created successfully');
            }

            setIsModalVisible(false);
            void fetchFeatureFlags();
        } catch (_error) {
            message.error('Failed to save feature flag');
        }
    };

    const columns = [
        {
            title: 'Key',
            dataIndex: 'key',
            key: 'key',
        },
        {
            title: 'Active For All',
            dataIndex: 'isActiveForAll',
            key: 'isActiveForAll',
            render: (isActiveForAll: boolean | null) => (
                <Switch checked={isActiveForAll || false} disabled />
            ),
        },
        {
            title: 'Active Facilities',
            dataIndex: 'activeFacilities',
            key: 'activeFacilities',
            render: (facilities: { id: string; name: string }[]) =>
                facilities.length > 0
                    ? facilities.map((f) => f.name).join(', ')
                    : 'None',
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_: any, record: FeatureFlagDto) => (
                <Space>
                    <Button
                        icon={<EditOutlined />}
                        onClick={() => handleEditFlag(record)}
                    />
                    <Button
                        icon={<DeleteOutlined />}
                        danger
                        onClick={() => {
                            Modal.confirm({
                                title: 'Delete Feature Flag',
                                content: `Are you sure you want to delete the feature flag "${record.key}"?`,
                                onOk: async () => {
                                    await handleDeleteFlag(record.key);
                                },
                            });
                        }}
                    />
                </Space>
            ),
        },
    ];

    if (!api || loading) {
        return <Spinner />;
    }

    return (
        <Layout>
            <Header>
                <h2>Feature Flags</h2>
            </Header>
            <Content>
                <div style={{ marginBottom: 16 }}>
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAddFlag}
                    >
                        Add Feature Flag
                    </Button>
                </div>

                <Table
                    dataSource={featureFlags}
                    columns={columns}
                    rowKey="key"
                />

                <Modal
                    title={
                        editingFlag ? 'Edit Feature Flag' : 'Add Feature Flag'
                    }
                    open={isModalVisible}
                    onOk={() => void handleModalOk()}
                    onCancel={() => setIsModalVisible(false)}
                    okText={editingFlag ? 'Update' : 'Create'}
                >
                    <Form form={form} layout="vertical">
                        <Form.Item
                            name="key"
                            label="Key"
                            rules={[
                                {
                                    required: true,
                                    message: 'Please enter a key',
                                },
                            ]}
                        >
                            <AutoComplete
                                placeholder="Select or type a flag key"
                                options={FEATURE_FLAGS.map((flag) => ({
                                    value: flag,
                                }))}
                                filterOption={(input, option) =>
                                    (option?.value ?? '')
                                        .toLowerCase()
                                        .includes(input.toLowerCase())
                                }
                            />
                        </Form.Item>
                        <Form.Item
                            name="isActiveForAll"
                            label="Active For All"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                        <Form.Item
                            name="activeFacilityIds"
                            label="Active Facilities"
                        >
                            <Select
                                mode="multiple"
                                placeholder="Select facilities"
                                style={{ width: '100%' }}
                                options={facilities.map((f) => ({
                                    label: f.name,
                                    value: f.id,
                                }))}
                            />
                        </Form.Item>
                    </Form>
                </Modal>
            </Content>
        </Layout>
    );
};
